import { useCallback, useState } from "react";
import {
  assembleWorkout,
  buildPatternsBySlug,
  notesForEditing,
  type Exercise,
  type LogSet,
  type Session,
  type Workout as WorkoutT,
} from "@heraion/shared";
import {
  canEditOnMobile,
  isNonTrivialDraft,
  loadPersistedLoggerState,
  setAsideLiveDraft,
  type EditingTarget,
} from "@heraion/ui";

import { apiFetch, ApiError } from "./api";
import { confirmViaWindow, notifyViaWindow } from "./confirm";

type Tr = (k: string, opts?: Record<string, unknown>) => string;

// Web twin of apps/mobile/src/screens/useSessionLoader.ts — same
// runLoad(sessionId, mode) shape, same shared hydrate path (RW-18's
// EditingTarget/mode on the shared LoggerScreen). Differences are all
// platform mechanism, never behaviour:
//   - Alert.alert (no-op on RNW) -> confirmViaWindow/notifyViaWindow, the
//     established web seam (see useLoggerHostDeps.ts). The mobile dialog's
//     cancel=resume/destructive=discard choice maps onto window.confirm's
//     Cancel/OK.
//   - no cache-first session-detail read: mobile's AsyncStorage cache
//     exists for opening at the gym offline; that scenario doesn't apply
//     to the web console, so this fetches straight from the API.
//   - no restoreWorkout/minimize: web-rnw has no minimize-workout surface
//     (foreground-service-only, native), so the "resume the in-progress
//     draft" branch just opens the Logger — there's nothing to un-minimize.
export function useSessionLoader(args: {
  ensureCatalog: () => Promise<Exercise[]>;
  setEditing: (target: EditingTarget | null) => void;
  setLoggerOpen: (open: boolean) => void;
  setError: (code: string | null) => void;
  t: Tr;
}): {
  openingEditId: string | null;
  repeatingId: string | null;
  onOpenEdit: (sessionId: string) => Promise<void>;
  onRepeat: (sessionId: string) => Promise<void>;
} {
  const { ensureCatalog, setEditing, setLoggerOpen, setError, t } = args;
  const [openingEditId, setOpeningEditId] = useState<string | null>(null);
  const [repeatingId, setRepeatingId] = useState<string | null>(null);

  const fetchSessionDetail = useCallback(
    async (sessionId: string): Promise<{ session: Session; sets: LogSet[] }> => {
      return apiFetch<{ session: Session; sets: LogSet[] }>(
        `/api/sessions/${encodeURIComponent(sessionId)}?include=sets`,
      );
    },
    [],
  );

  const runLoad = useCallback(
    async (
      sessionId: string,
      mode: "edit" | "repeat",
      setBusy: (id: string | null) => void,
    ) => {
      setBusy(sessionId);
      try {
        // Same guard as mobile: don't clobber a draft already in progress.
        // Opening edit/repeat seeds the reducer from this session and (for
        // repeat) overwrites the single persisted draft slot, or clears it
        // on save (edit) — the LG-10b data-loss shape. Gate on
        // isNonTrivialDraft, the same bar the persist effect writes at.
        const persisted = await loadPersistedLoggerState();
        if (persisted && isNonTrivialDraft(persisted.draft)) {
          const discard = await confirmViaWindow({
            title: t("mobile.logger.conflictTitle"),
            body: t("mobile.logger.conflictBody"),
            confirmLabel: t("mobile.logger.resumeDiscard"),
            cancelLabel: t("mobile.workouts.resumePillAction"),
            destructive: true,
          });
          if (!discard) {
            // Resume the in-progress draft instead of opening this session.
            setEditing(null);
            setLoggerOpen(true);
            return;
          }
          // Set aside, not destroyed (#288): the live draft leaves its slot
          // only once the copy reads back. A failed copy keeps it, and the
          // athlete lands on it instead.
          if (!(await setAsideLiveDraft())) {
            console.warn("[workouts] draft not set aside: resuming it instead");
            setEditing(null);
            setLoggerOpen(true);
            return;
          }
        }
        // #24: a correction opens with the session's saved notes, read
        // beside the detail.
        const notesRead = notesForEditing(apiFetch, sessionId, mode);
        const res = await fetchSessionDetail(sessionId);
        const workout: WorkoutT = assembleWorkout(res.session, res.sets);
        if (!canEditOnMobile(workout)) {
          notifyViaWindow(
            t("workout.editUnsupportedTitle"),
            t("workout.editUnsupportedBody"),
          );
          return;
        }
        // Best-effort: fetch the catalog so the post-save MAX-bump check on
        // the edit can read movement patterns. If the call fails we still
        // open the editor — the MAX-bump path silently skips entries with
        // unknown patterns.
        let patternsBySlug = {};
        try {
          patternsBySlug = buildPatternsBySlug(await ensureCatalog());
        } catch {
          patternsBySlug = {};
        }
        const notes = await notesRead;
        setEditing(
          mode === "repeat"
            ? { sessionId, workout, patternsBySlug, mode: "repeat" }
            : { sessionId, workout, patternsBySlug, ...(notes ? { notes } : {}) },
        );
        setLoggerOpen(true);
      } catch (err) {
        const e = err as ApiError;
        setError(`api_error_${e.status ?? 0}`);
      } finally {
        setBusy(null);
      }
    },
    [fetchSessionDetail, ensureCatalog, setEditing, setLoggerOpen, setError, t],
  );

  const onOpenEdit = useCallback(
    async (sessionId: string) => {
      if (openingEditId !== null) return;
      await runLoad(sessionId, "edit", setOpeningEditId);
    },
    [openingEditId, runLoad],
  );

  const onRepeat = useCallback(
    async (sessionId: string) => {
      if (repeatingId !== null || openingEditId !== null) return;
      await runLoad(sessionId, "repeat", setRepeatingId);
    },
    [repeatingId, openingEditId, runLoad],
  );

  return { openingEditId, repeatingId, onOpenEdit, onRepeat };
}
